import { useState, useEffect } from "react";
import api from "@/helpers/api";

export interface BannerState {
  dismissed: boolean;
  dismissedAt?: number | null;
  remindLater?: number | null;
  ratedUs?: boolean;
  activatedAt?: number | null;
  [key: string]: unknown; // Allow additional properties
}

export interface BannerConfig {
  slug: string;
  defaultState: BannerState;
  visibilityCheck?: (state: BannerState) => boolean;
}

export interface UseBannerReturn {
  isVisible: boolean;
  bannerState: BannerState;
  isLoading: boolean;
  updateState: (newState: Partial<BannerState>) => Promise<void>;
  handleDismiss: () => void;
}

export function useBanner(config: BannerConfig): UseBannerReturn {
  const { slug, defaultState, visibilityCheck } = config;
  
  const [bannerState, setBannerState] = useState<BannerState>(defaultState);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch banner state from WordPress on mount
  useEffect(() => {
    const fetchBannerState = async () => {
      try {
        setIsLoading(true);
        const response = await api.get({
          path: `/${slug}`,
        }) as BannerState;
        
        setBannerState(response);
      } catch (error) {
        console.error(`Error fetching ${slug} banner state:`, error);
        // Set default state on error
        setBannerState(defaultState);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBannerState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Update visibility whenever state changes
  useEffect(() => {
    if (isLoading) {
      setIsVisible(false);
      return;
    }

    if (visibilityCheck) {
      setIsVisible(visibilityCheck(bannerState));
    } else {
      // Default visibility check: show if not dismissed
      setIsVisible(!bannerState.dismissed);
    }
  }, [bannerState, isLoading, visibilityCheck]);

  // Update banner state in WordPress
  const updateState = async (newState: Partial<BannerState>) => {
    try {
      const updatedState = { ...bannerState, ...newState };
      
      await api.post({
        path: `/${slug}`,
        data: updatedState,
      });

      setBannerState(updatedState);
    } catch (error) {
      console.error(`Error updating ${slug} banner state:`, error);
    }
  };

  const handleDismiss = () => {
    const newState = {
      dismissed: true,
      dismissedAt: Date.now(),
    };

    updateState(newState);
    setIsVisible(false);
  };

  return {
    isVisible,
    bannerState,
    isLoading,
    updateState,
    handleDismiss,
  };
}

